import clsx from 'clsx';
import { array, func, string } from 'prop-types';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components';

const Stepper = ({ steps, onStepClick, className = '' }) => {
  const { t } = useTranslation();
  const currentStepIndex = useMemo(() => {
    const currentStep = steps.find((step) => step.current);
    return currentStep ? currentStep.order : null;
  }, [steps]);

  const stepChecked = (index) => {
    // Add step checked icon for previous valid and completed steps of the current step
    if (index < currentStepIndex) {
      return steps[index].valid && steps[index].completed;
    }
  };

  return (
    <div className={clsx('flex items-center lg:justify-center', className)}>
      {steps.map((step, index) => (
        <Fragment key={`step-${step.slug}`}>
          <div className="hidden lg:flex lg:flex-col lg:items-center">
            <div className="flex items-center">
              {/* Stepper Line Before */}
              <div
                className={clsx('w-16 h-0.5', {
                  'bg-transparent': index === 0,
                  'bg-primary-500': index !== 0 && index <= currentStepIndex,
                  'bg-gray-400': index > currentStepIndex
                })}
              />

              {/* Stepper Circle */}
              <button
                type="button"
                onClick={() => index <= currentStepIndex && onStepClick(index)}
                data-test-id={`step-${index}`}
                className={clsx('flex items-center justify-center rounded-full', {
                  'w-4 h-4 bg-primary-500 text-white cursor-pointer': index < currentStepIndex,
                  'w-4 h-4 bg-white border-4 border-primary-500': index === currentStepIndex,
                  'w-2 h-2 m-1 bg-gray-400 cursor-default': index > currentStepIndex
                })}
              >
                {stepChecked(index) && <Icon name="check" classes="h-3" />}
              </button>

              {/* Stepper Line After */}
              <div
                className={clsx('w-16 h-0.5', {
                  'bg-transparent': index === steps.length - 1,
                  'bg-primary-500': index < currentStepIndex,
                  'bg-gray-400': index >= currentStepIndex && index !== steps.length - 1
                })}
              />
            </div>

            {/* Stepper Label */}
            <div
              className={clsx('text-sm text-center uppercase', {
                'text-primary-500': index === currentStepIndex,
                'text-gray-400': index !== currentStepIndex
              })}
            >
              {t(`purchase:stepper.${step.slug}`)}
            </div>
          </div>

          {/* Stepper Label - Mobile only */}
          {index === currentStepIndex && (
            <div className="h2 underlined-blue lg:hidden">{t(`purchase:stepper.${step.slug}`)}</div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

Stepper.propTypes = {
  steps: array.isRequired,
  onStepClick: func.isRequired,
  className: string
};

export default Stepper;
